import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { LayoutDashboard, Library, Dumbbell, Image, ClipboardCheck, Compass, ScrollText, Settings, LogOut, Search, Layers } from "lucide-react";
import { useState } from "react";
import { Logo } from "@/components/Brand";
import { useAuth } from "@/context/Auth";
import { ROLE_LABEL, can } from "@/lib/api";
import ReminderBell from "@/components/ReminderBell";

const NAV = [
  ["/", "Dashboard", LayoutDashboard], ["/library", "Protocol Library", Library], ["/bulk", "Bulk Create", Layers], ["/interventions", "Interventions", Dumbbell],
  ["/illustrations", "Illustrations", Image], ["/review", "Review Queue", ClipboardCheck], ["/suggest", "Suggest", Compass],
  ["/audit", "Audit Trail", ScrollText], ["/admin", "Admin", Settings],
];

export default function Layout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [q, setQ] = useState("");
  return (
    <div className="min-h-screen flex flex-col lg:flex-row">
      <aside className="no-print lg:w-60 lg:min-h-screen bg-white border-b lg:border-b-0 lg:border-r border-slate-200 flex lg:flex-col shrink-0">
        <div className="px-4 py-4 hidden lg:block"><Logo /></div>
        <nav className="flex lg:flex-col gap-0.5 px-2 py-2 overflow-x-auto w-full" data-testid="main-nav">
          {NAV.filter(([p]) => (p !== "/admin" || user?.role === "admin") && (p !== "/bulk" || can(user, "ai"))).map(([path, label, Icon]) => (
            <NavLink key={path} to={path} end={path === "/"} data-testid={`nav-${label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) => `flex items-center gap-2.5 whitespace-nowrap rounded px-3 py-2 text-[13px] font-medium transition-colors ${isActive ? "bg-sky-50 text-sky-800" : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"}`}>
              <Icon size={16} /> {label}
            </NavLink>
          ))}
        </nav>
        <div className="mt-auto hidden lg:block p-4 text-[10px] leading-relaxed text-slate-400 border-t border-slate-100">
          PranIQ is a facilitator and technology enabler. Content is authored and approved by qualified clinicians. No patient data is stored here.
        </div>
      </aside>
      <div className="flex-1 min-w-0 flex flex-col">
        <header className="no-print h-14 bg-white/80 backdrop-blur-md border-b border-slate-200 flex items-center gap-3 px-4 sm:px-6 sticky top-0 z-20">
          <div className="lg:hidden"><Logo size={26} sub={false} /></div>
          <form className="relative flex-1 max-w-md" onSubmit={(e) => { e.preventDefault(); nav(`/library?q=${encodeURIComponent(q)}`); }}>
            <Search size={15} className="absolute left-2.5 top-2.5 text-slate-400" />
            <input data-testid="global-search-input" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search protocols, conditions, ICD-10…"
              className="w-full h-9 rounded border border-slate-200 bg-slate-50 pl-8 pr-3 text-sm outline-none focus:border-sky-400 focus:bg-white" />
          </form>
          <div className="ml-auto flex items-center gap-3">
            {["admin", "reviewer", "approver"].includes(user?.role) && <ReminderBell />}
            <div className="hidden sm:block text-right leading-tight">
              <div className="text-[13px] font-semibold text-slate-800" data-testid="current-user-name">{user?.name}</div>
              <div className="text-[11px] text-slate-500" data-testid="current-user-role">{ROLE_LABEL[user?.role]}</div>
            </div>
            <button data-testid="logout-btn" onClick={logout} className="p-2 rounded hover:bg-slate-100 text-slate-500" title="Sign out"><LogOut size={16} /></button>
          </div>
        </header>
        <main className="print-main flex-1 p-4 sm:p-6 lg:p-8 min-w-0"><Outlet /></main>
      </div>
    </div>
  );
}
